import { useState, useCallback } from 'react';
import { Heart, HeartCrack, Snowflake } from 'lucide-react';
import type { FilaJukebox } from '@/shared/models/jukebox.model';
import { useVibeTakeoverFila } from '../hooks/useVibeTakeoverFila';

interface Props {
  telaId: string;
}

interface Particle {
  id:       string;
  type:     'romance' | 'sofrencia';
  x:        number;   // 5–92 (%) — posição horizontal
  duration: number;   // ms — duração da animação
  size:     number;   // rem — tamanho do emoji
}

const JANELA   = 10;
const LIMIAR   = 0.75;

function calcularVibes(fila: FilaJukebox[]) {
  const pendentes = fila.filter(f => f.status === 'pendente').slice(0, JANELA);
  if (pendentes.length === 0) return { romance: 0, sofrencia: 0, total: 0 };
  const romance   = pendentes.filter(f => f.vibe === 'romance').length;
  const sofrencia = pendentes.filter(f => f.vibe === 'sofrencia').length;
  return { romance, sofrencia, total: pendentes.length };
}

function randomBetween(min: number, max: number) {
  return min + Math.random() * (max - min);
}

export default function TvVibeTakeover({ telaId }: Props) {
  const [particles, setParticles] = useState<Particle[]>([]);

  const spawnParticles = useCallback((type: 'romance' | 'sofrencia') => {
    const count = 5 + Math.floor(Math.random() * 4); // 5–8 por voto
    const batch: Particle[] = Array.from({ length: count }, () => ({
      id:       Math.random().toString(36).slice(2),
      type,
      x:        randomBetween(5, 92),
      duration: randomBetween(1800, 2800),
      size:     randomBetween(2.0, 3.5),
    }));
    setParticles(prev => [...prev, ...batch]);
    setTimeout(() => {
      const ids = new Set(batch.map(p => p.id));
      setParticles(prev => prev.filter(p => !ids.has(p.id)));
    }, 3200);
  }, []);

  const fila = useVibeTakeoverFila(telaId, spawnParticles);

  const { romance, sofrencia, total } = calcularVibes(fila);
  const pctRomance   = total > 0 ? romance   / total : 0;
  const pctSofrencia = total > 0 ? sofrencia / total : 0;
  const romanceTakeover   = pctRomance   >= LIMIAR;
  const sofrenciaTakeover = pctSofrencia >= LIMIAR;

  return (
    <>
      {/* ── PARTÍCULAS — cada voto spawna emojis caindo ────────────────────────
          position:fixed direto no span, top:0 + translateY(-3.5rem) no keyframe
          garante que caem do topo sem overflow:hidden bloqueando a animação     */}
      {particles.map(p => (
        <span
          key={p.id}
          className="fixed top-0 z-[24] pointer-events-none select-none animate-vibe-particle-fall"
          style={{
            left:                    `${p.x}%`,
            fontSize:                `${p.size}rem`,
            animationDuration:       `${p.duration}ms`,
            animationTimingFunction: 'cubic-bezier(0.4, 0, 0.6, 1)',
            willChange:              'transform',
          }}
        >
          {p.type === 'romance' ? '❤️' : '💔'}
        </span>
      ))}

      {/* ── VINHETA DE DOMINAÇÃO (≥75%) — z-[14], abaixo de todos os overlays ─ */}
      {romanceTakeover && (
        <div className="fixed inset-0 z-[14] pointer-events-none">
          <div
            className="absolute inset-0"
            style={{
              background: 'radial-gradient(ellipse at 0% 50%, rgba(244,63,94,0.22) 0%, transparent 55%), radial-gradient(ellipse at 100% 50%, rgba(244,63,94,0.12) 0%, transparent 45%)',
            }}
          />
          <div className="absolute top-8 left-8 opacity-[0.07] animate-pulse-slow select-none">
            <Heart style={{ width: '7rem', height: '7rem' }} fill="currentColor" strokeWidth={0} className="text-rose-400" />
          </div>
        </div>
      )}
      {sofrenciaTakeover && (
        <div className="fixed inset-0 z-[14] pointer-events-none">
          <div
            className="absolute inset-0"
            style={{
              background: 'radial-gradient(ellipse at 100% 50%, rgba(6,182,212,0.20) 0%, transparent 55%), radial-gradient(ellipse at 0% 50%, rgba(6,182,212,0.10) 0%, transparent 45%)',
            }}
          />
          <div className="absolute top-8 right-8 opacity-[0.07] animate-pulse-slow select-none">
            <HeartCrack style={{ width: '7rem', height: '7rem' }} fill="currentColor" strokeWidth={0} className="text-cyan-400" />
          </div>
        </div>
      )}

      {/* ── HUD COMPACTO — z-[9], abaixo do logo (z-10) e do FutebolPlacar (z-20)
              bottom-36 (144px) posiciona o HUD acima do logo (h-24 + bottom-6 = 120px) ── */}
      {total >= 2 && (
        <div
          className={`
            fixed bottom-36 left-6 z-[9] pointer-events-none min-w-[200px]
            bg-black/60 backdrop-blur-md border rounded-2xl px-4 py-3
            transition-all duration-500
            ${romanceTakeover   ? 'border-rose-500/40 animate-vibe-hud-pulse' :
              sofrenciaTakeover ? 'border-cyan-500/40'                         :
                                  'border-white/10'}
          `}
        >
          <p className="text-[10px] font-black uppercase tracking-widest text-white/55 mb-2 text-center">
            Batalha de Jukebox
          </p>

          <div className="flex items-center justify-between gap-3 mb-2">
            <div className={`flex items-center gap-1.5 ${romanceTakeover ? 'text-rose-400' : 'text-rose-400/70'}`}>
              <Heart size={16} fill="currentColor" strokeWidth={0} aria-hidden />
              <span className="font-black text-lg tabular-nums leading-none">{romance}</span>
            </div>

            <span className="text-white/50 font-bold text-xs">VS</span>

            <div className={`flex items-center gap-1.5 ${sofrenciaTakeover ? 'text-cyan-400' : 'text-cyan-400/70'}`}>
              <span className="font-black text-lg tabular-nums leading-none">{sofrencia}</span>
              <HeartCrack size={16} fill="currentColor" strokeWidth={0} aria-hidden />
            </div>
          </div>

          <div className="h-2 rounded-full overflow-hidden bg-white/[0.06] flex">
            <div
              className="h-full bg-gradient-to-r from-rose-600 to-pink-500 transition-all duration-700 rounded-l-full"
              style={{ width: `${Math.round(pctRomance * 100)}%` }}
            />
            <div
              className="h-full bg-gradient-to-r from-cyan-500 to-blue-500 transition-all duration-700 rounded-r-full"
              style={{ width: `${Math.round(pctSofrencia * 100)}%` }}
            />
          </div>

          {(romanceTakeover || sofrenciaTakeover) && (
            <p className={`flex items-center justify-center gap-1 text-xs font-bold text-center mt-1.5 uppercase tracking-wider ${
              romanceTakeover ? 'text-rose-400' : 'text-cyan-400'
            }`}>
              {romanceTakeover
                ? <><Heart size={10} fill="currentColor" strokeWidth={0} aria-hidden /> Apaixonados dominando!</>
                : <><Snowflake size={10} aria-hidden /> Sofrência tomou conta!</>
              }
            </p>
          )}
        </div>
      )}
    </>
  );
}
